// cbrExport.js — Excel sheet + two graphs for CBR (IS 2720 Part 16:1987):
//   1) load–penetration curves (origin-corrected) with the 2.5 / 5 mm marks, 2) CBR vs dry density with 95 / 97 / 100 % of MDD.
const THIN = { style: 'thin', color: { argb: 'FF000000' } };
const BOX = { top: THIN, left: THIN, bottom: THIN, right: THIN };
const font = (size = 11, bold = false) => ({ name: 'Calibri', size, bold });
const COLORS = ['#1d4ed8', '#047857', '#b45309'];
const ROMAN = ['I', 'II', 'III'];
const utcDate = (iso) => { if (!iso) return null; const [y, m, d] = iso.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); };
const niceStep = (span) => { const raw = span / 8, p = 10 ** Math.floor(Math.log10(raw)); return [1, 2, 5, 10].map((k) => k * p).find((s) => s >= raw); };

// shared frame: white canvas, title, grid, axis labels. Returns px/py mappers and a clipped-drawing helper.
function frame(canvas, { title, xLo, xHi, yLo, yHi, xLabel, yLabel, xFmt = String, yFmt = String }) {
  const W = 1240, H = 800;
  canvas.width = W; canvas.height = H;
  const g = canvas.getContext('2d');
  g.fillStyle = '#fff'; g.fillRect(0, 0, W, H);
  const m = { l: 140, r: 60, t: 100, b: 130 };
  const px = (x) => m.l + ((x - xLo) / (xHi - xLo)) * (W - m.l - m.r);
  const py = (y) => H - m.b - ((y - yLo) / (yHi - yLo)) * (H - m.t - m.b);
  g.font = 'bold 34px Arial'; g.fillStyle = '#111'; g.textAlign = 'center'; g.fillText(title, W / 2, 55);
  g.lineWidth = 1; g.strokeStyle = '#d9d9d9'; g.fillStyle = '#333'; g.font = '24px Arial';
  const xs = niceStep(xHi - xLo), ys = niceStep(yHi - yLo);
  for (let x = xLo; x <= xHi + 1e-9; x += xs) { g.beginPath(); g.moveTo(px(x), m.t); g.lineTo(px(x), H - m.b); g.stroke(); g.textAlign = 'center'; g.fillText(xFmt(x), px(x), H - m.b + 34); }
  for (let y = yLo; y <= yHi + 1e-9; y += ys) { g.beginPath(); g.moveTo(m.l, py(y)); g.lineTo(W - m.r, py(y)); g.stroke(); g.textAlign = 'right'; g.fillText(yFmt(y), m.l - 14, py(y) + 8); }
  g.strokeStyle = '#111'; g.lineWidth = 2.5; g.strokeRect(m.l, m.t, W - m.l - m.r, H - m.t - m.b);
  g.fillStyle = '#111'; g.font = '28px Arial'; g.textAlign = 'center'; g.fillText(xLabel, (m.l + W - m.r) / 2, H - 30);
  g.save(); g.translate(44, (m.t + H - m.b) / 2); g.rotate(-Math.PI / 2); g.fillText(yLabel, 0, 0); g.restore();
  const clip = (fn) => { g.save(); g.beginPath(); g.rect(m.l, m.t, W - m.l - m.r, H - m.t - m.b); g.clip(); fn(); g.restore(); };
  return { g, m, W, H, px, py, clip };
}

export function drawLoadPenetration(canvas, { rows, title }) {
  const ymax = Math.max(...rows.flatMap((r) => r.curve.map((p) => p.kgcm2)), 1);
  const yHi = Math.ceil(ymax / 10) * 10;
  const f = frame(canvas, { title, xLo: 0, xHi: 12.5, yLo: 0, yHi, xLabel: 'Penetration (mm)', yLabel: 'Load (kg/cm²)', yFmt: (y) => y.toFixed(0) });
  const { g, px, py, m, H, clip } = f;
  clip(() => {
    g.setLineDash([12, 10]); g.strokeStyle = '#9ca3af'; g.lineWidth = 2;
    [2.5, 5].forEach((x) => { g.beginPath(); g.moveTo(px(x), py(0)); g.lineTo(px(x), py(yHi)); g.stroke(); });
    g.setLineDash([]);
    rows.forEach((r, i) => {
      const pts = r.curve.filter((p) => p.pen >= 0);
      g.strokeStyle = COLORS[i]; g.fillStyle = COLORS[i]; g.lineWidth = 4; g.beginPath();
      pts.forEach((p, j) => (j ? g.lineTo(px(p.pen), py(p.kgcm2)) : g.moveTo(px(p.pen), py(p.kgcm2)))); g.stroke();
      pts.forEach((p) => { g.beginPath(); g.arc(px(p.pen), py(p.kgcm2), 7, 0, Math.PI * 2); g.fill(); });
    });
  });
  g.font = '24px Arial'; g.textAlign = 'center'; g.fillStyle = '#4b5563';
  g.fillText('2.5 mm', px(2.5), m.t - 12); g.fillText('5.0 mm', px(5), m.t - 12);
  g.textAlign = 'left'; g.font = 'bold 22px Arial';
  const base = H - m.b - 24 - (rows.length - 1) * 32; // legend sits in the empty lower-right area
  rows.forEach((r, i) => { g.fillStyle = COLORS[i]; g.fillText(`Mould ${ROMAN[i]}:  ${r.cbr25.toFixed(1)} % @ 2.5 mm  ·  ${r.cbr50.toFixed(1)} % @ 5 mm`, px(6.4), base + i * 32); });
  return canvas;
}

export function drawCbrDensity(canvas, { rows, atMdd, mdd, title }) {
  const ds = rows.map((r) => r.dry).concat([mdd * 0.95, mdd]);
  const cs = rows.map((r) => r.design).concat(atMdd.map((a) => a.cbr));
  const xLo = Math.floor((Math.min(...ds) - 0.03) * 20) / 20, xHi = Math.ceil((Math.max(...ds) + 0.03) * 20) / 20;
  const yHi = Math.ceil((Math.max(...cs) * 1.15) / 5) * 5 || 5;
  const f = frame(canvas, { title, xLo, xHi, yLo: 0, yHi, xLabel: 'Dry density (g/cc)', yLabel: 'CBR (%)', xFmt: (x) => x.toFixed(2) });
  const { g, px, py, m, clip } = f;
  const sorted = [...rows].sort((a, b) => a.dry - b.dry);
  clip(() => {
    g.strokeStyle = '#1d4ed8'; g.lineWidth = 4; g.beginPath();
    sorted.forEach((r, i) => (i ? g.lineTo(px(r.dry), py(r.design)) : g.moveTo(px(r.dry), py(r.design)))); g.stroke();
    atMdd.forEach((a) => {
      g.setLineDash([12, 10]); g.strokeStyle = '#b91c1c'; g.lineWidth = 2.5;
      g.beginPath(); g.moveTo(px(a.dry), py(0)); g.lineTo(px(a.dry), py(a.cbr)); g.lineTo(px(xLo), py(a.cbr)); g.stroke(); g.setLineDash([]);
      g.fillStyle = '#b91c1c'; g.beginPath(); g.arc(px(a.dry), py(a.cbr), 9, 0, Math.PI * 2); g.fill();
    });
    g.fillStyle = '#111'; rows.forEach((r) => { g.beginPath(); g.arc(px(r.dry), py(r.design), 10, 0, Math.PI * 2); g.fill(); });
  });
  g.fillStyle = '#b91c1c'; g.font = 'bold 26px Arial'; g.textAlign = 'left';
  atMdd.forEach((a, i) => g.fillText(`${a.level} % MDD (${a.dry.toFixed(3)}) → CBR ${a.cbr.toFixed(1)} %${a.extrapolated ? '  (extrapolated)' : ''}`, m.l + 24, m.t + 44 + i * 36));
  return canvas;
}

export function addCBRSheet(wb, { header, inputs, result, images = [] }) {
  const mo = inputs.moulds, nM = mo.length, last = 1 + nM * 2;       // A label | per mould 2 columns
  const ws = wb.addWorksheet('CBR', { pageSetup: { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0 } });
  ws.getColumn(1).width = 40; for (let c = 2; c <= last; c++) ws.getColumn(c).width = 14;
  const left = { horizontal: 'left', vertical: 'middle', wrapText: true }, center = { horizontal: 'center', vertical: 'middle', wrapText: true };
  const area = (r1, c1, r2, c2, value, o = {}) => {
    for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) { const k = ws.getCell(r, c); k.border = o.border ?? BOX; k.font = o.font ?? font(); k.alignment = o.align ?? center; }
    const top = ws.getCell(r1, c1);
    if (value !== undefined && value !== null && value !== '') top.value = value;
    if (o.numFmt) top.numFmt = o.numFmt;
    if (r2 > r1 || c2 > c1) ws.mergeCells(r1, c1, r2, c2);
  };
  let r = 0;
  const single = (label, value, numFmt) => { r += 1; ws.getRow(r).height = 22; area(r, 1, r, 1, label, { font: font(11, true), align: left }); area(r, 2, r, last, value, { numFmt }); return r; };
  const perMould = (label, fn, numFmt, bold = false) => {
    r += 1; ws.getRow(r).height = 22; area(r, 1, r, 1, label, { font: font(11, bold), align: left });
    mo.forEach((m, i) => area(r, 2 + 2 * i, r, 3 + 2 * i, fn(m, i, ws.getColumn(2 + 2 * i).letter), { font: font(11, bold), numFmt })); return r;
  };

  r = 1; ws.getRow(1).height = 30;
  area(1, 1, 1, 1, 'Name of Project:-', { font: font(11, true), align: left }); area(1, 2, 1, last, header.project, { align: left });
  single('Issue to', header.issueTo);
  r += 1; area(r, 1, r, last, 'CALIFORNIA BEARING RATIO (CBR) — as per IS : 2720 (Part 16) : 1987', { font: font(12, true) }); ws.getRow(r).height = 26;
  single('Type of Material', header.material); single('Location / Offset', header.location); single('Sample No.', header.sampleNo);
  if (inputs.source) single('Source', inputs.source);
  if (inputs.proposedUse) single('Proposed use', inputs.proposedUse);
  single('Date of Receiving', utcDate(header.receivedOn), 'dd/mm/yyyy');
  if (inputs.castingOn) single('Date of Casting', utcDate(inputs.castingOn), 'dd/mm/yyyy');
  single('Date of Testing', utcDate(inputs.testedOn || header.testedOn), 'dd/mm/yyyy');
  single('Compaction / soaking', `${inputs.compaction === 'heavy' ? 'Heavy' : 'Light'} compaction · soaked ${inputs.soakHours} h · oversize replaced ${inputs.replacedOversizeKg} kg`);
  if (inputs.surchargeKg != null) single('Surcharge weight (kg)', inputs.surchargeKg, '0.0');
  const rRing = single('Proving ring factor (kg / div)', inputs.ringFactor, '0.000');
  const rArea = single('Plunger area (cm²)', inputs.areaCm2, '0.00');
  if (inputs.omcPct != null) single('OMC (%)', inputs.omcPct, '0.0');
  if (inputs.labMddGcc) single('Lab MDD (g/cc)', inputs.labMddGcc, '0.000');

  r += 1; area(r, 1, r, 1, 'Mould', { font: font(11, true), align: left });
  mo.forEach((_, i) => area(r, 2 + 2 * i, r, 3 + 2 * i, `Mould ${ROMAN[i]}`, { font: font(11, true) }));
  if (mo.some((m) => m.mouldNo != null)) perMould('Mould no.', (m) => m.mouldNo ?? '', '0');
  perMould('Blows per layer', (m) => m.blows, '0');
  const rMw = perMould('Weight of mould (gms)', (m) => m.mouldMassG, '0');
  const rMs = perMould('Weight of mould + compacted soil (gms)', (m) => m.mouldSoilG, '0');
  const rVol = perMould('Volume of mould (cc)', (m) => m.volumeCc, '0');
  const rWet = perMould('Wet density (g/cc)', (m, i, L) => ({ formula: `(${L}${rMs}-${L}${rMw})/${L}${rVol}`, result: result.rows[i].bulk }), '0.000');
  if (mo.some((m) => m.contNo != null)) perMould('Container no.', (m) => m.contNo ?? '', '0');
  const rCw = perMould('Container + wet soil (gms)', (m) => m.wetG, '0.00');
  const rCd = perMould('Container + dry soil (gms)', (m) => m.dryG, '0.00');
  const rCt = perMould('Container (gms)', (m) => m.tareG, '0.00');
  const rMc = perMould('Moisture content (%)', (m, i, L) => ({ formula: `(${L}${rCw}-${L}${rCd})/(${L}${rCd}-${L}${rCt})*100`, result: result.rows[i].w }), '0.00');
  perMould('Dry density (g/cc)', (m, i, L) => ({ formula: `${L}${rWet}*100/(100+${L}${rMc})`, result: result.rows[i].dry }), '0.000', true);
  if (inputs.labMddGcc) perMould('Compaction (% of lab MDD)', (m, i) => result.rows[i].compactionPct, '0.0');
  if (inputs.omcPct != null) perMould('Moisture − OMC (%)', (m, i) => result.rows[i].omcVar, '0.00');
  if (mo.some((m) => m.after)) {
    perMould('After soaking — mould + soil (gms)', (m) => m.after?.mouldSoilG ?? '', '0');
    if (mo.some((m) => m.after?.contNo != null)) perMould('After soaking — container no.', (m) => m.after?.contNo ?? '', '0');
    perMould('After soaking — container + wet (gms)', (m) => m.after?.wetG ?? '', '0.00');
    perMould('After soaking — container + dry (gms)', (m) => m.after?.dryG ?? '', '0.00');
    perMould('After soaking — container (gms)', (m) => m.after?.tareG ?? '', '0.00');
    perMould('After soaking — moisture content (%)', (m, i) => result.rows[i].after?.w ?? '', '0.00');
    perMould('After soaking — dry density (g/cc)', (m, i) => result.rows[i].after?.dry ?? '', '0.000', true);
  }
  const rDs = perMould('Swell dial reading — start (mm)', (m) => m.dsMm, '0.00');
  const rDr = perMould('Swell dial reading — end (mm)', (m) => m.drMm, '0.00');
  const rH = perMould('Specimen height (mm)', (m) => m.heightMm, '0.0');
  perMould('Expansion ratio (%)', (m, i, L) => ({ formula: `(${L}${rDr}-${L}${rDs})/${L}${rH}*100`, result: result.rows[i].expansionRatio }), '0.00', true);
  perMould('Origin shift (mm)', (m) => m.originShiftMm, '0.00');

  // penetration table
  r += 1; ws.getRow(r).height = 22;
  area(r, 1, r, 1, 'Penetration (mm)', { font: font(11, true), align: left });
  mo.forEach((_, i) => { area(r, 2 + 2 * i, r, 2 + 2 * i, `Dial ${ROMAN[i]}`, { font: font(11, true) }); area(r, 3 + 2 * i, r, 3 + 2 * i, 'kg/cm²', { font: font(11, true) }); });
  const pens = [...new Set(mo.flatMap((m) => m.readings.map((x) => x.pen)))].sort((a, b) => a - b);
  pens.forEach((pen) => {
    r += 1; area(r, 1, r, 1, pen, { numFmt: '0.0', align: left });
    mo.forEach((m, i) => {
      const rd = m.readings.find((x) => x.pen === pen), D = ws.getColumn(2 + 2 * i).letter;
      area(r, 2 + 2 * i, r, 2 + 2 * i, rd ? rd.dial : '', { numFmt: '0.0' });
      area(r, 3 + 2 * i, r, 3 + 2 * i, rd ? { formula: `${D}${r}*$B$${rRing}/$B$${rArea}`, result: (rd.dial * inputs.ringFactor) / inputs.areaCm2 } : '', { numFmt: '0.00' });
    });
  });
  perMould('CBR at 2.5 mm (%)', (m, i) => result.rows[i].cbr25, '0.0');
  perMould('CBR at 5.0 mm (%)', (m, i) => result.rows[i].cbr50, '0.0');
  perMould('CBR of mould (%)', (m, i) => result.rows[i].design, '0.0', true);
  if (result.atMdd) result.atMdd.forEach((a) => single(`CBR at ${a.level} % of MDD (${a.dry.toFixed(3)} g/cc)${a.extrapolated ? ' — extrapolated' : ''}`, a.cbr, '0.0'));
  r += 1; ws.getRow(r).height = 26;
  area(r, 1, r, 1, result.reportedLabel, { font: font(13, true), align: left }); area(r, 2, r, last, result.reportedCbr, { font: font(13, true), numFmt: '0.0' });

  let row = r + 1;
  images.forEach((b64) => { const id = wb.addImage({ base64: b64, extension: 'png' }); ws.addImage(id, { tl: { col: 0, row }, ext: { width: 620, height: 400 } }); row += 21; });
  r = row + 1;
  area(r, 1, r, last, 'CBR at each penetration = (unit load ÷ standard load of 70 kg/cm² at 2.5 mm or 105 kg/cm² at 5 mm) × 100, after the origin shift; the higher value is adopted (IS 2720-16 7.3). Values at 95 / 97 / 100 % of MDD are interpolated between moulds.',
    { font: font(9), align: left, border: {} }); ws.getRow(r).height = 30;
  r += 1; area(r, 1, r, last, `Checked by: ${header.checkedBy}   |   Tested by: ${header.testedBy}`, { font: font(9), align: left, border: {} });
  r += 2; ws.getRow(r).height = 44; area(r, 1, r, last, 'Authorised Signatory', { font: font(12, true), align: { horizontal: 'right', vertical: 'middle' }, border: {} });
  return ws;
}

export const cbrExporter = {
  async addSheet(wb, { header, data }) {
    const { inputs, result } = data;
    const tag = `${header.sampleNo || ''} ${header.location || ''}`.trim();
    const c1 = drawLoadPenetration(document.createElement('canvas'), { rows: result.rows, title: `Load–penetration — ${tag}` });
    const toBlob = (c) => new Promise((res) => c.toBlob(res, 'image/png'));
    const files = [{ suffix: 'CBR_load_penetration.png', blob: await toBlob(c1) }];
    const images = [c1.toDataURL('image/png')];
    if (result.atMdd) {
      const c2 = drawCbrDensity(document.createElement('canvas'), { rows: result.rows, atMdd: result.atMdd, mdd: result.labMddGcc, title: `CBR vs dry density — ${tag}` });
      images.push(c2.toDataURL('image/png')); files.push({ suffix: 'CBR_vs_density.png', blob: await toBlob(c2) });
    }
    addCBRSheet(wb, { header, inputs, result, images });
    return files;
  },
};
